import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import {
  DeviceListResponse,
  DeviceRetireEnvelope,
  DeviceRetirementResponse,
  DeviceShowResponse,
} from '../../../Interfaces/api/backoffice/device.interface';
import { Device } from '../../../Models/backoffice/device.model';
import { DeviceDetail } from '../../../Models/backoffice/device-detail.model';
import { DeviceFilters } from '../../Organizations/Interfaces/device-filters.interface';

@Injectable({
  providedIn: 'root',
})
export class DeviceEstateService {
  private readonly _apiService = inject(ApiService);

  listRequest(
    organizationId: string,
    filters: DeviceFilters,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<DeviceListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<DeviceListResponse>>(
      endpoints.backoffice.organizationDevices(organizationId),
      { params: this._params(filters, page, pageSize), skipLoading: true, skipError: true },
    );
  }

  showRequest(
    organizationId: string,
    deviceId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<DeviceShowResponse>> {
    return this._apiService.get<LaravelDefaultResponse<DeviceShowResponse>>(
      endpoints.backoffice.organizationDevice(organizationId, deviceId),
      { skipLoading: true, skipError: true },
    );
  }

  retire(
    organizationId: string,
    deviceId: string,
    reason: string,
  ): Observable<DeviceRetirementResponse> {
    const request = this._apiService.delete<LaravelDefaultResponse<DeviceRetireEnvelope>>(
      endpoints.backoffice.organizationDevice(organizationId, deviceId),
      { body: { reason }, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<DeviceRetireEnvelope>>(request)
      .pipe(map((response) => response.data.retirement));
  }

  toDevices(response: LaravelDefaultResponse<DeviceListResponse> | undefined): Device[] {
    return (response?.data?.devices ?? []).map((row) => new Device(row));
  }

  toCounts(
    response: LaravelDefaultResponse<DeviceListResponse> | undefined,
  ): Record<string, number> {
    return response?.data?.counts ?? {};
  }

  toTotal(response: LaravelDefaultResponse<DeviceListResponse> | undefined): number {
    return response?.data?.total ?? 0;
  }

  toDetail(
    response: LaravelDefaultResponse<DeviceShowResponse> | undefined,
  ): DeviceDetail | null {
    const row = response?.data?.device;

    return row ? new DeviceDetail(row) : null;
  }

  private _params(
    filters: DeviceFilters,
    page: number,
    pageSize: number,
  ): Record<string, string | number> {
    const params: Record<string, string | number> = { page, pageSize };

    for (const [key, value] of Object.entries(filters)) {
      if (value !== '') {
        params[key] = String(value);
      }
    }

    return params;
  }
}
